/**
 * User Service - API calls for user management (admin)
 * 
 * Backend endpoints expected:
 * GET    /api/admin/users         - List all users (paginated)
 * GET    /api/admin/users/:id     - Get single user
 * PUT    /api/admin/users/:id     - Update user
 * DELETE /api/admin/users/:id     - Delete user
 * POST   /api/admin/users         - Create user (admin only)
 */

import api from './api';

// Development mode flag
const USE_MOCK = process.env.REACT_APP_USE_MOCK === 'true';

// Mock users data
let MOCK_USERS = [
  {
    id: 1,
    email: 'admin@annpriya.com',
    name: 'Admin User',
    role: 'admin',
    status: 'active',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=admin',
    phone: '+91 98765 43210',
    address: 'Book my fresh Farm, Bangalore',
    createdAt: '2024-01-15T10:00:00Z',
    lastLogin: '2026-03-03T08:30:00Z',
    ordersCount: 0,
    totalSpent: 0
  },
  {
    id: 2,
    email: 'john@example.com',
    name: 'John Doe',
    role: 'user',
    status: 'active',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=john',
    phone: '+91 98765 11111',
    address: '123 Main St, Mumbai',
    createdAt: '2024-06-20T14:30:00Z',
    lastLogin: '2026-03-02T18:45:00Z',
    ordersCount: 12,
    totalSpent: 45600
  },
  {
    id: 3,
    email: 'priya@example.com',
    name: 'Priya Sharma',
    role: 'user',
    status: 'active',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=priya',
    phone: '+91 87654 22222',
    address: '456 Park Ave, Delhi',
    createdAt: '2024-08-10T09:15:00Z',
    lastLogin: '2026-03-01T12:00:00Z',
    ordersCount: 8,
    totalSpent: 32400
  },
  {
    id: 4,
    email: 'rahul@example.com',
    name: 'Rahul Patel',
    role: 'user',
    status: 'inactive',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=rahul',
    phone: '+91 76543 33333',
    address: '789 Lake View, Chennai',
    createdAt: '2024-09-05T16:45:00Z',
    lastLogin: '2025-12-15T10:30:00Z',
    ordersCount: 3,
    totalSpent: 12800
  },
  {
    id: 5,
    email: 'anita@example.com',
    name: 'Anita Gupta',
    role: 'user',
    status: 'active',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=anita',
    phone: '+91 65432 44444',
    address: '321 Green Lane, Pune',
    createdAt: '2024-11-12T11:20:00Z',
    lastLogin: '2026-03-03T07:15:00Z',
    ordersCount: 15,
    totalSpent: 68900
  },
  {
    id: 6,
    email: 'vikram@example.com',
    name: 'Vikram Singh',
    role: 'user',
    status: 'suspended',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=vikram',
    phone: '+91 54321 55555',
    address: '654 Hill Road, Hyderabad',
    createdAt: '2024-12-01T08:00:00Z',
    lastLogin: '2026-01-20T14:00:00Z',
    ordersCount: 1,
    totalSpent: 2400
  }
];

const userService = {
  /**
   * Get all users with optional filtering
   */
  async getUsers({ page = 1, limit = 10, search = '', role = '', status = '' } = {}) {
    if (USE_MOCK) {
      await new Promise(resolve => setTimeout(resolve, 300));
      
      let filtered = [...MOCK_USERS];
      
      if (search) {
        const s = search.toLowerCase();
        filtered = filtered.filter(u => 
          u.name.toLowerCase().includes(s) || 
          u.email.toLowerCase().includes(s)
        );
      }
      
      if (role) {
        filtered = filtered.filter(u => u.role === role);
      }
      
      if (status) {
        filtered = filtered.filter(u => u.status === status);
      }
      
      const total = filtered.length;
      const start = (page - 1) * limit;
      const users = filtered.slice(start, start + limit);
      
      return {
        success: true,
        data: {
          users,
          pagination: {
            page,
            limit,
            total,
            pages: Math.ceil(total / limit)
          }
        }
      };
    }
    
    try {
      const response = await api.get('/admin/users', {
        params: { page, limit, search, role, status }
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  /**
   * Get single user by ID
   */
  async getUser(id) {
    if (USE_MOCK) {
      await new Promise(resolve => setTimeout(resolve, 200));
      const user = MOCK_USERS.find(u => u.id === id);
      if (!user) {
        return { success: false, error: 'User not found' };
      }
      return { success: true, data: user };
    }
    
    try {
      const response = await api.get(`/admin/users/${id}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  /**
   * Update user
   */
  async updateUser(id, updates) {
    if (USE_MOCK) {
      await new Promise(resolve => setTimeout(resolve, 300));
      const index = MOCK_USERS.findIndex(u => u.id === id);
      if (index === -1) {
        return { success: false, error: 'User not found' };
      }
      MOCK_USERS[index] = { ...MOCK_USERS[index], ...updates };
      return { success: true, data: MOCK_USERS[index] };
    }
    
    try {
      const response = await api.put(`/admin/users/${id}`, updates);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  },

  /**
   * Create new user (admin only)
   */
  async createUser(userData) {
    if (USE_MOCK) {
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Check if email exists
      if (MOCK_USERS.some(u => u.email.toLowerCase() === userData.email.toLowerCase())) {
        return { success: false, error: 'Email already exists' };
      }
      
      const newUser = {
        id: Math.max(...MOCK_USERS.map(u => u.id)) + 1,
        email: userData.email,
        name: userData.name,
        role: userData.role || 'user',
        status: userData.status || 'active',
        avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${userData.email}`,
        phone: userData.phone || '',
        address: userData.address || '',
        createdAt: new Date().toISOString(),
        lastLogin: null,
        ordersCount: 0,
        totalSpent: 0
      };
      
      MOCK_USERS.push(newUser);
      return { success: true, data: newUser };
    }
    
    try {
      const response = await api.post('/admin/users', userData);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  },

  /**
   * Delete user
   */
  async deleteUser(id) {
    if (USE_MOCK) {
      await new Promise(resolve => setTimeout(resolve, 300));
      const index = MOCK_USERS.findIndex(u => u.id === id);
      if (index === -1) {
        return { success: false, error: 'User not found' };
      }
      MOCK_USERS = MOCK_USERS.filter(u => u.id !== id);
      return { success: true };
    }
    
    try {
      await api.delete(`/admin/users/${id}`);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  },

  /**
   * Change user role
   */
  async changeRole(id, role) {
    return this.updateUser(id, { role });
  },

  /**
   * Change user status
   */
  async changeStatus(id, status) {
    return this.updateUser(id, { status });
  },

  /**
   * Get user stats for dashboard
   */
  async getStats() {
    if (USE_MOCK) {
      await new Promise(resolve => setTimeout(resolve, 200));
      const total = MOCK_USERS.length;
      const active = MOCK_USERS.filter(u => u.status === 'active').length;
      const admins = MOCK_USERS.filter(u => u.role === 'admin').length;
      const newThisMonth = MOCK_USERS.filter(u => {
        const created = new Date(u.createdAt);
        const now = new Date();
        return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
      }).length;
      
      return {
        success: true,
        data: { total, active, admins, newThisMonth }
      };
    }
    
    try {
      const response = await api.get('/admin/users/stats');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  uploadAvatar: async (file) => {
    const formData = new FormData();
    formData.append('avatar', file);
    try {
      const response = await api.post('/users/me/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    }
  },
};

export default userService;
